<script setup lang="ts">
import AddToWishlist from '@/components/common/AddToWishlist.vue'
import CartItemNotesModal from '@/components/common/CartItemNotesModal.vue'
import CloseButton from '@/components/common/CloseButton.vue'
import QuantityEditor from '@/components/common/QuantityEditor.vue'
import QuantitySuggestion from '@/components/product-details/QuantitySuggestion.vue'
import ShareButton from '@/components/product-details/ShareButton.vue'
import { usePrice } from '@/composables/price'
import { useScreenSize } from '@/composables/screen-size'
import { useTracker } from '@/composables/tracker'
import useAuthStore from '@/stores/auth'
import useCartStore from '@/stores/cart'
import useProductStore from '@/stores/product'
import { AppBlock, AppButton, AppDivider, useDialog } from 'lilasia-ui'
import { computed, defineAsyncComponent } from 'vue'
import { useI18n } from 'vue-i18n'

const { formatMoney } = usePrice()
const { trackAddToCart } = useTracker()
const { showDialog } = useDialog()
const { t } = useI18n()
const { isMobile, isTablet } = useScreenSize()

const authStore = useAuthStore()
const cartStore = useCartStore()
const productStore = useProductStore()

const itemTotal = computed(() => {
  if (productStore.selectedProduct?.price && cartStore.quantity > 0) {
    return productStore.selectedProduct?.price * cartStore.quantity
  }
  return productStore.selectedProduct?.price
})

const notesButtonText = computed(() => {
  if (isMobile) {
    return ''
  }

  return t('notes')
})

const handleInput = (value: number | string) => {
  if (typeof value !== 'number') {
    cartStore.quantity = 1
  }
}

const showCartItemNotesModal = () => {
  if (!authStore.isAuthenticated) {
    authStore.showAuthModal()
    return
  }

  showDialog(CartItemNotesModal)
  if (isMobile) {
    productStore.showPurchaseOptions = false
  }
}

const addToCart = () => {
  if (!authStore.isAuthenticated) {
    if (productStore.selectedProduct) {
      trackAddToCart(productStore.selectedProduct, cartStore.quantity, true)
    }
    authStore.showAuthModal()
    return
  }

  if (productStore.selectedProduct) {
    if (cartStore.productExistedInCart) {
      const quantity = cartStore.productExistedInCart.quantity + cartStore.quantity
      cartStore.updateItem({
        product: productStore.selectedProduct,
        quantity,
        notes: cartStore.productExistedInCart.notes
      })

      trackAddToCart(productStore.selectedProduct, quantity)
    } else {
      cartStore.addItem({
        product: productStore.selectedProduct,
        quantity: cartStore.quantity,
        notes: cartStore.notes
      })

      cartStore.notes = null
      trackAddToCart(productStore.selectedProduct, cartStore.quantity)
    }

    // Showing Added To Cart Status for a 3 seconds
    productStore.addedToCartStatus = true
    setTimeout(() => {
      productStore.addedToCartStatus = false
    }, 3000)

    if (isMobile) {
      productStore.showPurchaseOptions = false
    }

    showDialog(
      defineAsyncComponent(() => import('@/components/product-details/AddedToCartModal.vue')),
      { position: 'middle' }
    )
  }
}

const buyNow = () => {
  if (!authStore.isAuthenticated) {
    authStore.showAuthModal()
    return
  }

  if (productStore.selectedProduct) {
    cartStore.checkoutItem({
      product: productStore.selectedProduct,
      quantity: cartStore.quantity,
      notes: cartStore.notes
    })

    cartStore.notes = null
  }
}
</script>

<template>
  <AppBlock
    :bordered="!isMobile"
    :rounded="undefined"
    class="rounded-[0] rounded-t-8 md:rounded-12"
  >
    <div class="flex items-center justify-between gap-16 px-24 py-16">
      <div
        class="text-16 font-600 leading-24 md:text-16 md:leading-24"
        :class="{ 'text-20': isMobile }"
      >
        {{ t('purchase_options') }}
      </div>
      <CloseButton
        v-if="isMobile"
        @click="productStore.showPurchaseOptions = false"
      />
    </div>
    <div class="px-24">
      <QuantitySuggestion />
    </div>

    <!-- Price and Quantity -->
    <div
      class="flex items-center justify-between gap-16 px-24 py-16 md:flex-col md:items-start lg:flex-row lg:items-center"
    >
      <div class="text-24 font-600 leading-32">
        {{ formatMoney(itemTotal) }}
      </div>
      <QuantityEditor
        id="item-quantity"
        v-model="cartStore.quantity"
        :block="isTablet"
        @update:model-value="handleInput"
      />
    </div>

    <!-- Add Notes and Add to Cart -->
    <div
      class="grid grid-cols-2 items-center gap-16 px-24 py-16 md:grid-cols-1"
      :class="{ 'bg-black-5': isMobile }"
    >
      <AppButton
        color="yellow"
        outline
        :text="t('buy_now')"
        block
        :loading="cartStore.checkingOut"
        @click="buyNow"
      />

      <AppButton
        color="yellow"
        :icon="productStore.addedToCartStatus ? 'check' : 'add'"
        :text="productStore.addedToCartStatus ? t('added_to_cart') : t('add_to_cart')"
        block
        @click="addToCart"
      />
    </div>

    <AppDivider class="hidden md:block" />

    <!-- Favorite and Share Buttons. -->
    <div
      class="hidden items-center justify-evenly gap-4 px-24 py-16 md:flex md:flex-col lg:flex-row"
    >
      <AppButton
        v-if="!isMobile"
        icon="edit"
        color="light"
        :text="notesButtonText"
        @click="showCartItemNotesModal"
      />

      <AddToWishlist
        v-if="productStore.selectedProduct?.id"
        :product="productStore.selectedProduct"
        color="light"
        :text="t('wishlist')"
        :block="isTablet"
      />

      <ShareButton />
    </div>
  </AppBlock>
</template>
